import { useEffect, useSyncExternalStore } from "react";

import { shippedLanguage } from "./catalog.ts";
import { deviceLocale, languageForLocale } from "./language-by-country.ts";
import {
  LANGUAGE_STORAGE_KEY,
  languageFromPath,
  resolveLanguage,
  urlLanguageEnabled,
} from "./language-preference.ts";
import type { Language } from "./language-preference.ts";
import { serverPath } from "./ssr-location.ts";

// The language every mounted `useTranslations` call reads right now, kept
// exactly the way design-system's theme store keeps light/dark/system
// (`theme-preference.ts`): one `localStorage` key, a listener set, no
// context provider to wrap the app in. The sign-in switcher writes it with
// nobody signed in yet; signing in overwrites it with the account's own
// saved choice (S6.04, "applied on sign-in"); the preferences panel writes
// it again, straight after a successful save, so the choice takes effect
// without a reload. Whoever reads it also keeps `<html lang>` in step.

const listeners = new Set<() => void>();

// Nobody chose yet: the device's country picks (S10.08), worked out once per page load.
let detected: Language | undefined;
const firstVisitLanguage = (): Language => (detected ??= languageForLocale(deviceLocale()));

// The website's own address carries the language under "/es/" (ADR-027 point 5): a URL in that
// shape wins over anything stored, on the server and on the client alike. The platform's routes
// never take that shape, so its stored preference, below, keeps deciding there.
const read = (): Language =>
  languageFromPath(window.location?.pathname ?? "/") ??
  (urlLanguageEnabled()
    ? resolveLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY))
    : resolveLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY), firstVisitLanguage()));

// `useSyncExternalStore`'s third argument is called both on an actual server render (no
// `window`, so the URL comes from `serverPath()`) and on the client's hydration pass, which
// must answer exactly what `read()` would: the hydration render already has a real `window`,
// so reading the real URL is both correct and matches it (ADR-027 point 5).
const serverRead = (): Language =>
  languageFromPath(typeof window === "undefined" ? serverPath() : window.location.pathname) ??
  resolveLanguage(null);

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Back and forward move the website between "/" and "/es/…" too.
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

/** The active language outside React: a toast, an error explained in a callback. */
export function currentLanguage(): Language {
  return typeof window === "undefined" ? serverRead() : read();
}

export function setActiveLanguage(language: Language): void {
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  notify();
}

/**
 * Tells every mounted `useActiveLanguage` to re-read the URL (`router.ts`'s `navigate` calls
 * this after an in-app navigation changes it), so a language a new "/es/" prefix names takes
 * effect at once, with no reload.
 */
export function notifyLocationChanged(): void {
  notify();
}

export function useActiveLanguage(): Language {
  const language = useSyncExternalStore(subscribe, read, serverRead);
  // The page says which language it is in, so a screen reader speaks Spanish
  // as Spanish and the browser spellchecks and hyphenates it as Spanish.
  useEffect(() => {
    document.documentElement.lang = shippedLanguage(language);
  }, [language]);
  return language;
}

/**
 * Sets `<html lang>` to the stored or detected language outside React, synchronously (D952):
 * `createUi`'s `render()` calls this before the first paint, so the page is never marked "en"
 * to the browser past the moment the preference is already knowable in `localStorage` — the
 * window `useActiveLanguage`'s own effect used to leave open until the first commit, during
 * which Chrome's auto-translate could decide the page is English and later garble a Spanish
 * screen's own copy mid-session (production incident 2026-10-27). A no-op outside the browser.
 */
export function syncDocumentLanguage(): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = shippedLanguage(currentLanguage());
}
